import { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from '@material-ui/core/TextField';
import { green } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { Typography } from "@material-ui/core";
import SingleSelect from "../InputStyled/singleSelect";
import { Box, Grid } from "@material-ui/core";
import { useMemo } from "react";
import { i18n } from "../../translate/i18n";
import * as Yup from "yup";

const useStyles = makeStyles(theme => ({
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const startWithContactMessage = [{
    name: i18n.t("conexaBots.modal.startWithContactMessage.true"),
    value: "1"
}, {
    name: i18n.t("conexaBots.modal.startWithContactMessage.false"),
    value: "0"
}];

const ConexaBotStoreModal = ({ open, onClose, conexaBotId }) => {

    const classes = useStyles();
    const [connections, setConnections] = useState([]);
    const initialState = {
        name: "",
        status: true,
        startWithContactMessage: "1",
        connectionId: null,
        inactivityMinutes: "",
        inactivityAnnotationMessage: "",
        inactivityAction: "finish",
        inactivityQueueId: "",
        inactivityConclusionId: ""
    };
    const [conexaBot, setConexaBot] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [conclusionMessages, setConclusionMessages] = useState([]);

    const ConexaBotSchema = Yup.object().shape({
        name: Yup.string()
            .required(`${i18n.t("conexaBots.modal.form.name")} ${i18n.t("forms.required")}`)
            .min(3, `${i18n.t("conexaBots.modal.form.name")} ${i18n.t("forms.min")}`),
        connectionId: Yup.string().nullable()
            .required(`${i18n.t("conexaBots.modal.form.connection")} ${i18n.t("forms.required")}`),
        startWithContactMessage: Yup.boolean()
            .required(`${i18n.t("conexaBots.modal.form.startWithContactMessage")} ${i18n.t("forms.required")}`),
        inactivityAction: Yup.string().when("inactivityMinutes", {
            is: (value) => value >= 1,
            then: schema =>
                schema
                    .oneOf(["finish", "transfer"], i18n.t("conexaBots.modal.form.messages.selectAction"))
                    .required(i18n.t("conexaBots.modal.form.messages.selectAction")),
            otherwise: schema => schema.notRequired()
        }),
        inactivityQueueId: Yup.string().when(
            ["inactivityMinutes", "inactivityAction"],
            {
                is: (minutes, action) =>
                    minutes >= 1 && action === "transfer",
                then: schema =>
                    schema.required(i18n.t("conexaBots.modal.form.messages.selectAction")),
                otherwise: schema => schema.notRequired()
            }
        ),
        inactivityConclusionId: Yup.string().when(
            ["inactivityMinutes", "inactivityAction"],
            {
                is: (minutes, action) =>
                    minutes >= 1 && action === "finish",
                then: schema =>
                    schema.required(i18n.t("conexaBots.modal.form.messages.selectFinalization")),
                otherwise: schema => schema.notRequired()
            }
        )
        
    });
    // reordenar para seguir sequencia correta
    if (ConexaBotSchema['_nodes']) ConexaBotSchema['_nodes'] = ConexaBotSchema['_nodes'].reverse();

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        (async () => {
            try {
                const { data } = await api.get(`/whatsapp/`, {
                    signal: abortController.signal,
                });
                if (!isMounted) return;
                setConnections(data);
            } catch (err) {
                if (!isMounted || err.name == 'AbortError') return;
                console.error({ err });
                toastError(err);
            }
        })();

        (async () => {
            try {
                const { data } = await api.get(`/conclusionMessagesAll/`, {
                    signal: abortController.signal,
                });
                if (!isMounted) return;
                setConclusionMessages(data.conclusionMessages);
            } catch (err) {
                if (!isMounted || err.name == 'AbortError') return;
                console.error({ err });
                toastError(err);
            }
        })();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        (async () => {
            if (!conexaBotId) {
                setConexaBot(initialState);
                return;
            }

            try {
                const { data } = await api.get(`/conexaBot/${conexaBotId}`, {
                    signal: abortController.signal,
                });
                if (!isMounted) return;

                setConexaBot(prevState => ({
                    ...prevState,
                    name: data.name,
                    status: data.status,
                    startWithContactMessage: data.startWithContactMessage == true ? "1" : "0",
                    connectionId: data.connectionId,
                    inactivityMinutes: data?.inactivityMinutes || "",
                    inactivityAnnotationMessage: data?.inactivityAnnotationMessage || "",
                    inactivityAction: data?.inactivityAction || "",
                    inactivityQueueId: data?.inactivityQueueId || "",
                    inactivityConclusionId: data?.inactivityConclusionId || ""
                }));
            } catch (err) {
                if (!isMounted || err.name == 'AbortError') return;
                console.error({ err });
                toastError(err);
            }
        })();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [conexaBotId, open]);

    const connectionQueues = useMemo(() => {
        if (!conexaBot?.connectionId) return [];

        const whatsapp = connections.find(
            w => w.id === conexaBot.connectionId
        );

        if (!Array.isArray(whatsapp?.Queues)) return [];

        return whatsapp.Queues.filter(
            q => q.name?.toUpperCase() !== "FILA_CHATBOT"
        );
    }, [conexaBot?.connectionId, connections]);

    const handleClose = () => {
        onClose();
        setConexaBot(initialState);
        setIsSubmitting(false);
    };

    const handleSaveChatbot = async () => {
        setIsSubmitting(true);
        let validated = false;
        try {

            await ConexaBotSchema.validate(conexaBot);
			validated = true;

            if (conexaBotId) {
                await api.put(`/conexaBot/${conexaBotId}`, { ...conexaBot, startWithContactMessage: conexaBot.startWithContactMessage == "1" ? true : false });
                toast.success('Atualizado com sucesso!');
                handleClose();
            } else {
                await api.post("/conexaBot", { ...conexaBot, startWithContactMessage: conexaBot.startWithContactMessage == "1" ? true : false });
                toast.success('Cadastrado com sucesso!');
                handleClose();
            }

        } catch (err) {
            console.info({err})
			if (validated == false) toastError({ response: { data: { error: err?.message } } });
            else toastError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderConnectionOptions = () => {
        const response = [];
        if (Array.isArray(connections)) {
            let ctmp = connections.filter(c => c.state = true);
            ctmp.map((w) => {
                response.push({
                    key: w.id,
                    value: w.name,
                    disabled: w.status != 'CONNECTED'
                });
            });
        }
        return response;
    }

    return (
        <Dialog
            open={open == true ? true : false}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle id="form-dialog-title">
                {conexaBotId ? i18n.t("conexaBots.modal.titles.update") : i18n.t("conexaBots.modal.titles.create")}
            </DialogTitle>
            <DialogContent dividers>

                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12}>
                        <Box
                            style={{ marginBottom: "10px" }}
                        >
                            <Typography style={{ display: "inline-block" }}>{i18n.t("conexaBots.modal.form.name")}</Typography>
                            <Typography
                                component="span"
                                style={{ marginLeft: 5, lineHeight: "normal" }}
                                color="error"
                            >
                                *
                            </Typography>
                        </Box>
                        <TextField
                            name="chatbotName"
                            id="chatbotName"
                            fullWidth
                            variant="outlined"
                            placeholder={i18n.t("conexaBots.modal.form.messages.name")}
                            inputProps={{ maxLength: 100 }}
                            onChange={e =>
                                setConexaBot({ ...conexaBot, name: e.target.value })
                            }
                            value={conexaBot.name}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12}>
                        <SingleSelect
                            title={
                                <Typography>
                                    <Typography style={{ display: "inline-block" }}>{i18n.t("conexaBots.modal.form.connection")}</Typography>
                                    <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                                </Typography>
                            }
                            displayText={i18n.t("conexaBots.modal.form.messages.selectConnection")}
                            name="chatbotConnectionId"
                            key="chatbotConnectionId"
                            options={renderConnectionOptions()}
                            onSelected={(opt) => {
                                setConexaBot(cBot => ({
                                    ...cBot,
                                    connectionId: opt,
                                }))
                            }}
                            selected={conexaBot.connectionId}
                            closeOnSelect={true}
                            disabled={Boolean(conexaBotId)}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12}>
                        <SingleSelect
                            title={
                                <Typography>
                                    <Typography style={{ display: "inline-block" }}>{i18n.t("conexaBots.modal.form.startWithContactMessage")}</Typography>
                                    <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                                </Typography>
                            }
                            displayText={i18n.t("conexaBots.modal.form.messages.selectStartWithContactMessage")}
                            name="chatbotConnectionstartWithContactMessage"
                            key="chatbotConnectionstartWithContactMessage"
                            options={startWithContactMessage.map((t) => { return { key: t.value, value: t.name, disabled: false } })}
                            onSelected={(opt) =>
                                setConexaBot(cht => ({
                                    ...cht,
                                    startWithContactMessage: opt,
                                }))
                            }
                            selected={conexaBot.startWithContactMessage}
                            closeOnSelect={true}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12}>
                        <Box
                            style={{ marginBottom: "10px" }}
                        >
                            <Typography style={{ display: "inline-block" }}>{i18n.t("conexaBots.modal.form.inactivity")}</Typography>
                        </Box>
                        <TextField
                            name="chatbotInactivityMinutes"
                            id="chatbotInactivityMinutes"
                            fullWidth
                            variant="outlined"
                            placeholder={i18n.t("conexaBots.modal.form.messages.inactivityMinutes")}
                            onChange={(e) => {
                                const rawValue = e.target.value;

                                // Permitir campo vazio
                                if (rawValue === "") {
                                    setConexaBot({
                                        ...conexaBot,
                                        inactivityMinutes: ""
                                    });
                                    return;
                                }

                                // Manter apenas números
                                const numericValue = Number(rawValue.replace(/\D/g, ""));

                                if (Number.isNaN(numericValue)) return;

                                let finalValue = numericValue;

                                if (numericValue < 1) finalValue = 1;
                                else if (numericValue > 4320) finalValue = 4320;

                                setConexaBot({
                                    ...conexaBot,
                                    inactivityMinutes: finalValue
                                });
                            }}
                            value={conexaBot.inactivityMinutes}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb={2} hidden={conexaBot.inactivityMinutes == ""}>
                    <Grid item xs={12}>
                        <Box
                            style={{ marginBottom: "10px" }}
                        >
                            <Typography style={{ display: "inline-block" }}>{i18n.t("conexaBots.modal.form.annotation")}</Typography>
                        </Box>
                        <TextField
                            name="chatbotInactivityAnnotationMessage"
                            id="chatbotInactivityAnnotationMessage"
                            fullWidth
                            variant="outlined"
                            placeholder={i18n.t("conexaBots.modal.form.messages.annotation")}
                            inputProps={{ maxLength: 255 }}
                            onChange={e =>
                                setConexaBot({ ...conexaBot, inactivityAnnotationMessage: e.target.value })
                            }
                            value={conexaBot.inactivityAnnotationMessage}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb={2} hidden={conexaBot.inactivityMinutes == ""}>
                    <Grid item xs={12}>
                        <SingleSelect
                            title={
                                <Typography>
                                    <Typography style={{ display: "inline-block" }}>{i18n.t("conexaBots.modal.form.action")}</Typography>
                                    <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                                </Typography>
                            }
                            displayText={i18n.t("conexaBots.modal.form.messages.action")}
                            name="chatbotInactivityAction"
                            key="chatbotInactivityAction"
                            options={[{ key: "finish", name: i18n.t("conexaBots.modal.form.inactivityActions.finish") }, { key: "transfer", name: i18n.t("conexaBots.modal.form.inactivityActions.transfer") }].map((t) => { return { key: t.key, value: t.name, disabled: false } })}
                            onSelected={(opt) => setConexaBot({ ...conexaBot, inactivityAction: opt })}
                            selected={conexaBot.inactivityAction}
                            closeOnSelect={true}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb={2} hidden={conexaBot.inactivityMinutes == "" || conexaBot.inactivityAction != "transfer"}>
                    <Grid item xs={12}>
                        <SingleSelect
                            title={
                                <Typography>
                                    <Typography style={{ display: "inline-block" }}>{i18n.t("conexaBots.modal.form.queue")}</Typography>
                                    <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                                </Typography>
                            }
                            displayText={i18n.t("conexaBots.modal.form.messages.queue")}
                            name="chatbotInactivityQueueId"
                            key="chatbotInactivityQueueId"
                            options={Array.isArray(connectionQueues) ? connectionQueues.map((c) => { return { key: c.id, value: c.name, disabled: false } }) : []}
                            onSelected={(opt) => setConexaBot({ ...conexaBot, inactivityQueueId: opt })}
                            selected={conexaBot.inactivityQueueId}
                            closeOnSelect={true}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb={2} hidden={conexaBot.inactivityMinutes == "" || conexaBot.inactivityAction != "finish"}>
                    <Grid item xs={12}>
                        <SingleSelect
                            title={
                                <Typography>
                                    <Typography style={{ display: "inline-block" }}>{i18n.t("conexaBots.modal.form.finalization")}</Typography>
                                    <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                                </Typography>
                            }
                            displayText={i18n.t("conexaBots.modal.form.messages.finalization")}
                            name="chatbotInactivityConclusionId"
                            key="chatbotInactivityConclusionId"
                            options={Array.isArray(conclusionMessages) ? conclusionMessages.map((c) => { return { key: c.id, value: c.shortcut, disabled: false } }) : []}
                            onSelected={(opt) => setConexaBot({ ...conexaBot, inactivityConclusionId: opt })}
                            selected={conexaBot.inactivityConclusionId}
                            closeOnSelect={true}
                        />
                    </Grid>
                </Grid>

            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting}
                    variant="outlined"
                >
                    {'Cancelar'}
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                    onClick={handleSaveChatbot}
                >
                    {conexaBotId
                        ? `${'Salvar'}`
                        : `${'Cadastrar'}`}
                    {isSubmitting && (
                        <CircularProgress
                            size={24}
                            className={classes.buttonProgress}
                        />
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConexaBotStoreModal;